import type { ProductJourney } from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { AccountTypeBadge } from "@shared/components/Yaas/AccountTypeBadge";
import {
    useSolanaAddressVerification,
    VERIFICATION_TONE_CLASS
} from "@shared/hooks/useSolanaAddressVerification";
import { SolanaAddress } from "@shared/utils/address";
import { truncateAddress } from "@shared/utils/format";
import {
    Button,
    Card,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    Text
} from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import type { SharedDestination } from "../state/types";

/**
 * Inline "add a destination" form for the Rewards Routing panel.
 *
 * Renders directly beneath the "+Add Destination" toggle that opens it,
 * on a raised `Card`, so it reads as a distinct sub-section belonging to
 * that action rather than more rows in the allocation list.
 *
 * Why it exists: the split grid is built from saved destinations, so a
 * user who wanted to route to a new address previously had to leave
 * this screen, add it in Rewards Destinations, and come back. This lets
 * them add one in place, and the new entry becomes an allocatable row
 * immediately.
 *
 * Mirrors the field stack in `EarnContent`'s address-book form (label
 * input + `RewardsDestinationField` + "+Add Account") rather than
 * inventing a second look for the same job. It is a separate component
 * rather than an extraction of that one because EarnContent's copy owns
 * a lot of surrounding tab/panel state; this form owns only its two
 * fields and delegates the write.
 *
 * Creating a saved destination is its own signed ceremony, distinct
 * from submitting the splits — so adding an address here does NOT
 * allocate it. The user still sets percentages and signs the routing
 * update separately.
 */
export function AddDestinationForm({
    analytics: entryAnalytics,
    existingAddresses,
    isCreating,
    onCreate,
    onAdded,
    onCancel
}: {
    analytics?: ProductJourney;
    /** Addresses already in the book — drives the duplicate guard. */
    existingAddresses: string[];
    isCreating: boolean;
    onCreate: (input: {
        name: string;
        address: string;
        analytics?: ProductJourney;
    }) => Promise<
        | { success: true; destination: SharedDestination }
        | { success: false; error: string }
    >;
    /** Fired after a successful create so the parent can close the form. */
    onAdded: () => void;
    /** Dismiss without creating. Sits beside the submit rather than
     *  below the whole panel — cancel belongs to the form it cancels. */
    onCancel: () => void;
}): React.ReactElement {
    const localAnalytics = useProductJourney(
        "saved_destination",
        "routing_panel"
    );
    const analytics = entryAnalytics ?? localAnalytics;
    useProductObservation(analytics, "form_viewed");
    const [label, setLabel] = useState("");
    const [address, setAddress] = useState("");
    const [isLabelFocused, setIsLabelFocused] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Run the verification at the call site and render it beside the
    // submit, per RewardsDestinationField's `hideOnChainStatus` note.
    // The hook runs inside the field regardless and dedupes on the same
    // trimmed input, so calling it here costs nothing extra.
    const verification = useSolanaAddressVerification(address);
    const trimmed = address.trim();
    const alreadyAdded = existingAddresses.some(
        (existing) => existing === trimmed
    );
    const canAdd =
        SolanaAddress.isShape(trimmed) && !alreadyAdded && !isCreating;

    useProductObservation(analytics, "validation_updated", {
        address_valid: SolanaAddress.isShape(trimmed),
        can_submit: canAdd,
        reason: alreadyAdded ? "duplicate" : undefined
    });
    const handleAdd = async (): Promise<void> => {
        if (!canAdd) return;
        setError(null);
        // Label is optional — fall back to a truncated address so the
        // row has something to display in the grid.
        const name = label.trim() || truncateAddress(trimmed, 4, 4);
        const result = await onCreate({ name, address: trimmed, analytics });
        if (!result.success) {
            setError(result.error);
            return;
        }
        setLabel("");
        setAddress("");
        analytics.close("completed");
        onAdded();
    };

    return (
        <Card
            border={false}
            /* A lightbar Input paints its surface on an internal
               wrapper span, not on the <input> that receives
               `className` — and no `surface` value maps to transparent
               ("transparent" aliases ground). Targeting the wrapper by
               its data-slot is the only lever short of changing the
               vendored design system, which `dependencies:sync:design`
               would overwrite. Scoped to this card so nothing else
               inherits it. */
            className="mt-8 p-3 flex flex-col gap-3 [&_[data-slot=input-lightbar]]:bg-transparent"
        >
            <Text
                variant="body"
                render={<span />}
                className="text-foreground-base"
            >
                Add Destination Address
            </Text>
            <div className="flex flex-col gap-1">
                {/* Design-system fields, lightbar interaction. `size`
                    fixes the shell height, so the address field no
                    longer grows when the account badge appears — the
                    badge is an addon inside a fixed-height group rather
                    than content that pushes the box taller. */}
                <Input
                    interaction="lightbar"
                    size="md"
                    /* Placeholder-only labelling loses the accessible
                       name on focus (the placeholder is cleared), so
                       name the fields explicitly. */
                    aria-label="Destination nickname (optional)"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onFocus={() => setIsLabelFocused(true)}
                    onBlur={() => {
                        setIsLabelFocused(false);
                        analytics.changed("changed", {
                            has_label: !!label.trim(),
                            address_valid: SolanaAddress.isShape(trimmed)
                        });
                    }}
                    placeholder={
                        isLabelFocused ? "" : "Destination Nickname (Optional)"
                    }
                />
                {/* Padding moves to the group, and the children space
                    themselves with gap-1. Left on the defaults the addon
                    and the input each carry their own px-3, so the badge
                    and the address sat ~24px apart inside the shell. */}
                <InputGroup className="px-3 gap-1 bg-transparent">
                    {/* Leads the address it labels. Only mounts once the
                        chain check resolves an account type, but the
                        group's height is fixed either way. */}
                    {verification.accountType && (
                        <InputGroupAddon align="inline-start" className="px-0">
                            <AccountTypeBadge type={verification.accountType} />
                        </InputGroupAddon>
                    )}
                    <InputGroupInput
                        interaction="lightbar"
                        size="md"
                        aria-label="Destination address"
                        /* px-0 — the group owns the horizontal padding.
                           text-ellipsis so a clipped address ends in "…"
                           rather than a hard cut, making it read as
                           continuing. */
                        className="px-0 text-ellipsis"
                        value={address}
                        onBlur={() =>
                            analytics.changed("changed", {
                                has_label: !!label.trim(),
                                address_valid: SolanaAddress.isShape(trimmed)
                            })
                        }
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Address"
                        spellCheck={false}
                        autoCapitalize="off"
                        autoCorrect="off"
                        state={
                            verification.tone === "error"
                                ? "error"
                                : verification.tone === "success"
                                  ? "success"
                                  : verification.tone === "warning"
                                    ? "warning"
                                    : undefined
                        }
                    />
                </InputGroup>
                {/* Everything status-shaped shares this one row, to the
                    left of the actions: the account badge, then the
                    dedup warning / create failure / on-chain result.
                    The field's own row is suppressed (`hideStatusRow`)
                    so none of it renders twice. Text precedence is
                    most-blocking first. */}
                <div className="mt-5 flex items-center justify-between gap-3">
                    {/* Status text only — the account badge now lives
                        inside the input, labelling the address it
                        describes. */}
                    <div className="flex items-center gap-2 min-w-0">
                        {alreadyAdded ? (
                            <Text
                                variant="fine-print"
                                render={<span />}
                                className="text-error-base"
                            >
                                Address already added
                            </Text>
                        ) : error ? (
                            <Text
                                variant="fine-print"
                                render={<span />}
                                className="text-error-base"
                            >
                                {error}
                            </Text>
                        ) : verification.text ? (
                            <Text
                                variant="fine-print"
                                render={<span />}
                                className={`${VERIFICATION_TONE_CLASS[verification.tone]} truncate`}
                            >
                                {verification.text}
                            </Text>
                        ) : (
                            <span />
                        )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <Button
                            variant="sunken"
                            interaction="lifted"
                            size="sm"
                            outline
                            disabled={!canAdd}
                            onClick={() => {
                                void handleAdd();
                            }}
                        >
                            {isCreating ? "Adding..." : "Add Address"}
                        </Button>
                        <Button
                            variant="link"
                            size="sm"
                            disabled={isCreating}
                            onClick={() => {
                                analytics.close("cancel");
                                onCancel();
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
        </Card>
    );
}
